import { useState } from "react";
import { Autocomplete, Paper, TextField, Typography } from "@mui/material";
import { captureError } from "src/utils/errorReporting";
import { usePlacesAutocompleteSuggestions, fetchPlaceDetails } from "src/utils/googlePlaces";
import { useT } from "src/i18n";

// Google Maps Platform policy: Places suggestions shown without a Google map
// must carry the "Google Maps" attribution (register: EXTERNAL_DATA_TERMS.md).
function GoogleMapsPaper({ children, ...props }) {
    return (
        <Paper {...props}>
            {children}
            <Typography variant="caption" sx={{ display: "block", textAlign: "right", px: 1.5, pb: 0.5, color: "text.secondary" }}>
                Google Maps
            </Typography>
        </Paper>
    );
}

/**
 * Reusable Google Places address autocomplete.
 *
 * Props:
 *   value            — controlled bound address string (shown when user is not typing)
 *   onValueChange    — (newText) => void — called on every input keystroke
 *   onPick           — async ({ lat, lng, formattedAddress, addressComponents, placeId }) => void
 *                      called after a successful fetchPlaceDetails; may be async
 *   label            — TextField label (defaults to the localized "Street address" copy)
 *   required         — boolean, passed to TextField
 *   error            — boolean, passed to TextField
 *   helperText       — string, passed to TextField
 *   textFieldSx      — sx prop forwarded to the inner TextField
 *   slotProps        — merged into Autocomplete's slotProps (e.g. custom paper styles)
 *   renderInput      — optional full override; if omitted the default TextField is rendered
 *   captureErrorTags — { entity, operation } — Sentry tags on fetchPlaceDetails failure
 *                      (defaults to { entity: "address", operation: "fetchPlaceDetails" })
 */
export default function AddressAutocomplete({
    value = "",
    onValueChange,
    onPick,
    label,
    required = false,
    error = false,
    helperText,
    textFieldSx,
    slotProps,
    captureErrorTags,
}) {
    const t = useT();
    const resolvedLabel = label ?? t("common.addressAutocomplete.label");
    const [typed, setTyped] = useState(null);
    const inputValue = typed !== null ? typed : value;

    const { suggestions, loading, ready, setQuery, rotateSession } =
        usePlacesAutocompleteSuggestions({ country: "fi", debounceMs: 250 });

    async function pick(picked) {
        if (!picked?.placeId) return;
        try {
            setTyped(picked.description);
            setQuery("");
            const details = await fetchPlaceDetails(picked);
            rotateSession();
            setTyped(null);
            if (onPick) {
                await onPick({
                    lat: details.lat,
                    lng: details.lng,
                    formattedAddress: details.formattedAddress,
                    addressComponents: details.addressComponents,
                    placeId: picked.placeId,
                    description: picked.description,
                });
            }
        } catch (err) {
            setTyped(null);
            captureError(err, {
                tags: captureErrorTags ?? { entity: "address", operation: "fetchPlaceDetails" },
                extra: { placeId: picked.placeId },
            });
        }
    }

    return (
        <Autocomplete
            freeSolo
            fullWidth
            openOnFocus={false}
            disableClearable
            slots={{ paper: GoogleMapsPaper }}
            options={suggestions}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.description ?? ""))}
            isOptionEqualToValue={(opt, val) => {
                if (!opt || !val) return false;
                if (typeof val === "string") return opt.description === val;
                return opt.placeId === val.placeId;
            }}
            inputValue={inputValue}
            onInputChange={(_, v, reason) => {
                if (reason === "input") {
                    setTyped(v);
                    setQuery(v);
                    if (onValueChange) onValueChange(v);
                } else if (reason === "clear") {
                    setTyped("");
                    setQuery("");
                    if (onValueChange) onValueChange("");
                }
            }}
            onChange={(_, picked, reason) => {
                if (reason === "selectOption" && picked && typeof picked !== "string") {
                    pick(picked);
                }
            }}
            loadingText={t("common.loading")}
            noOptionsText={ready ? t("common.addressAutocomplete.noSuggestions") : t("common.addressAutocomplete.typeFreely")}
            slotProps={slotProps}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={resolvedLabel}
                    required={required}
                    error={error}
                    helperText={helperText}
                    sx={textFieldSx}
                    slotProps={{
                        ...params.slotProps,
                        htmlInput: {
                            ...params.slotProps.htmlInput,
                            autoComplete: "street-address",
                            "data-bwignore": "true",
                        },
                    }}
                />
            )}
        />
    );
}
